/** The Bay Area's hexagons over the basemap, each coloured by its value in `values`
 *  (hexagon id -> number, through `colorOf`); hexagons without one stay empty. A click on
 *  a hexagon calls `onSelect(id)`, on empty map `onSelect(null)`. `describe(cell)` gives
 *  the tooltip's lines. `bands`, useBands' { outlines }, draws the protected
 *  neighbourhoods over the hexagons. Read-only. */

import { useMemo, useRef } from 'react';
import { DeckGL } from '@deck.gl/react';
import { Map } from 'react-map-gl';
import { PolygonLayer } from '@deck.gl/layers';
import { REGION_VIEW, bandLayers } from './WorkshopMap';
import { useReleaseWebGL } from './webgl';

export const EMPTY = [255, 255, 255, 10];
export const EDGE = [255, 255, 255, 40];
export const PICKED_FILL = [255, 255, 255, 230];
export const PICKED_EDGE = [124, 198, 68, 255];
export const HIGHLIGHT = [124, 198, 68, 110];

export default function HexMap({ cells, values, colorOf, selectedId, onSelect, describe, bands, legend, children }) {
  const host = useRef(null);
  useReleaseWebGL(host);
  const outlines = bands ? bands.outlines : null;
  const bandParts = useMemo(() => bandLayers('ws-hex', outlines), [outlines]);

  const layers = useMemo(() => [
    new PolygonLayer({
      id: 'ws-hexes',
      data: cells,
      getPolygon: (c) => c.polygon,
      filled: true,
      stroked: true,
      lineWidthUnits: 'pixels',
      getFillColor: (c) => {
        if (c.id === selectedId) return PICKED_FILL;
        const v = values ? values.get(c.id) : undefined;
        return v === undefined ? EMPTY : [...colorOf(v), 130];
      },
      getLineColor: (c) => (c.id === selectedId ? PICKED_EDGE : EDGE),
      getLineWidth: (c) => (c.id === selectedId ? 3 : 0.5),
      pickable: true,
      autoHighlight: true,
      highlightColor: HIGHLIGHT,
      updateTriggers: {
        getFillColor: [selectedId, values, colorOf],
        getLineColor: [selectedId],
        getLineWidth: [selectedId],
      },
    }),
    ...bandParts,
  ].filter(Boolean), [cells, values, colorOf, selectedId, bandParts]);

  const getTooltip = ({ object }) => (object ? describe(object).join('\n') : null);

  return (
    <div className="ws-map" ref={host}>
      <DeckGL
        initialViewState={REGION_VIEW}
        controller={true}
        layers={layers}
        getTooltip={getTooltip}
        onClick={(info) => onSelect(info.object ? info.object.id : null)}
      >
        <Map
          mapStyle="mapbox://styles/mapbox/dark-v10"
          customAttribution='<a href="https://511.org" target="_blank" rel="noopener noreferrer">Bay Area transit: data provided by 511.org</a>'
        />
      </DeckGL>
      {children}
      {legend}
    </div>
  );
}
